import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api/client.js'
import type { Crumb, ObjectContext, ObjectKind, ObjectNode } from '../api/types.js'

export const KIND_LABEL: Record<ObjectKind, string> = {
  customer: 'Kunde',
  site: 'Liegenschaft',
  building: 'Gebäude',
  unit: 'Einheit',
  component: 'Bauteil',
}

export const KIND_PLURAL: Record<ObjectKind, string> = {
  customer: 'Kunden',
  site: 'Liegenschaften',
  building: 'Gebäude',
  unit: 'Einheiten',
  component: 'Bauteile',
}

export const KIND_ORDER: ObjectKind[] = ['customer', 'site', 'building', 'unit', 'component']

/** Levels may be skipped, so a site can hold an Einheit directly. */
export function childKinds(parent: ObjectKind | null): ObjectKind[] {
  if (parent === null) return ['customer']
  // Four levels: Kunde, Liegenschaft, Gebäude, Einheit. No new Bauteile.
  if (parent === 'unit') return []
  return KIND_ORDER.slice(KIND_ORDER.indexOf(parent) + 1).filter((kind) => kind !== 'component')
}

/** One letter per level, the tree stays readable without five icons. */
export function KindBadge({ kind, title }: { kind: ObjectKind; title?: string }) {
  return (
    <span className={`kind kind-${kind}`} title={title ?? KIND_LABEL[kind]} aria-label={KIND_LABEL[kind]}>
      {KIND_LABEL[kind][0]}
    </span>
  )
}

export function formatAddress(a: { street: string | null; zip: string | null; city: string | null }): string {
  return [a.street, [a.zip, a.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')
}

/**
 * "Liegenschaft, Haus 3 › Wohnung 3.02", the two lines every list uses to
 * say where something is. On the site itself the second line is left out.
 */
export function ObjectPlace({ context, compact }: { context: ObjectContext; compact?: boolean }) {
  return (
    <span className="place">
      <span className={compact ? undefined : 'strong'}>{context.siteName}</span>
      {context.path.length > 0 && (
        <span className="muted small" style={{ display: compact ? 'inline' : 'block' }}>
          {compact ? ', ' : ''}
          {context.path.join(' › ')}
        </span>
      )}
    </span>
  )
}

export function crumbLabel(breadcrumb: Crumb[] | undefined, node: { name: string }): string {
  const parts = (breadcrumb ?? []).filter((c) => c.kind !== 'customer').map((c) => c.name)
  return [...parts, node.name].join(' › ')
}

// ---------------------------------------------------------------- Auswahl

interface PickerProps {
  value: string
  onChange: (id: string, node: ObjectNode | null) => void
  /** Leave out customers when picking a place for a task. */
  taskable?: boolean
  placeholder?: string
  autoFocus?: boolean
  /** Show only this kind (e.g. `site` for the report link form). */
  kind?: ObjectKind
  required?: boolean
}

/**
 * A search box instead of a 3,000-entry dropdown.
 *
 * Typing a name, a street, a Wohnungsnummer or a Youtilly ID lists matching
 * nodes with their path, so "Haus 3" in three different Liegenschaften stays
 * tellable apart. Without input the Liegenschaften are offered as a start.
 */
export function ObjectPicker({ value, onChange, taskable = true, placeholder, autoFocus, kind, required }: PickerProps) {
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const debounced = useDebounced(term, 180)

  const params = new URLSearchParams()
  if (debounced.trim()) params.set('q', debounced.trim())
  else params.set('kind', kind ?? 'site')
  if (kind) params.set('kind', kind)
  params.set('limit', '40')

  const results = useQuery({
    queryKey: ['object-search', params.toString()],
    queryFn: () => api<ObjectNode[]>(`/api/objects?${params}`),
    enabled: open,
    placeholderData: (previous) => previous,
  })

  const selected = useQuery({
    queryKey: ['object', value],
    queryFn: () => api<ObjectNode & { breadcrumb: Crumb[] }>(`/api/objects/${value}`),
    enabled: Boolean(value),
    staleTime: 5 * 60_000,
  })

  const options = useMemo(
    () => (results.data ?? []).filter((node) => !taskable || node.kind !== 'customer'),
    [results.data, taskable],
  )

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => setActive(0), [debounced])

  function choose(node: ObjectNode) {
    onChange(node.id, node)
    setTerm('')
    setOpen(false)
  }

  if (value && selected.data && !open) {
    const node = selected.data
    return (
      <div className="picker picked">
        <KindBadge kind={node.kind} />
        <span className="picked-text">
          <span className="strong">{node.name}</span>
          {node.breadcrumb.length > 0 && (
            <span className="muted small">, {crumbLabel(node.breadcrumb.filter((c) => c.kind !== 'customer'), { name: '' }).replace(/ › $/, '')}</span>
          )}
        </span>
        <button
          type="button"
          className="btn ghost small"
          onClick={() => {
            onChange('', null)
            setOpen(true)
          }}
        >
          Ändern
        </button>
      </div>
    )
  }

  return (
    <div className="picker" ref={wrapRef}>
      <input
        type="search"
        value={term}
        required={required && !value}
        autoFocus={autoFocus}
        placeholder={placeholder ?? 'Liegenschaft, Adresse, Wohnungsnummer oder Youtilly-ID …'}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setTerm(event.target.value)
          setOpen(true)
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setActive((i) => Math.min(i + 1, options.length - 1))
          } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActive((i) => Math.max(i - 1, 0))
          } else if (event.key === 'Enter' && open && options[active]) {
            event.preventDefault()
            choose(options[active]!)
          } else if (event.key === 'Escape') {
            setOpen(false)
          }
        }}
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {open && (
        <ul className="picker-list" role="listbox">
          {results.isLoading && <li className="muted small">Suche …</li>}
          {!results.isLoading && options.length === 0 && (
            <li className="muted small">{debounced ? 'Nichts gefunden.' : 'Noch keine Objekte angelegt.'}</li>
          )}
          {options.map((node, index) => (
            <li
              key={node.id}
              role="option"
              aria-selected={index === active}
              className={index === active ? 'active' : undefined}
              onMouseEnter={() => setActive(index)}
              onMouseDown={(event) => {
                event.preventDefault()
                choose(node)
              }}
            >
              <KindBadge kind={node.kind} />
              <span className="text">
                <span className="strong">{node.name}</span>
                <span className="muted small">
                  {[
                    ...(node.breadcrumb ?? []).filter((c) => c.kind !== 'customer').map((c) => c.name),
                    formatAddress(node) || null,
                    node.number ? `Nr. ${node.number}` : null,
                    node.externalRef ? `ID ${node.externalRef}` : null,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}
